import React from "react";
import { X } from "lucide-react";

const CustomerDetails = ({ customer, onClose }) => {
  const formatStatus = (status) => {
    const statusMap = {
      0: { label: "Inativo", class: "bg-red-100 text-red-800" },
      1: { label: "Ativo", class: "bg-green-100 text-green-800" },
      2: { label: "Suspenso", class: "bg-yellow-100 text-yellow-800" },
      3: { label: "Em análise", class: "bg-blue-100 text-blue-800" },
      4: { label: "Regular", class: "bg-green-100 text-green-800" },
    };
    return (
      statusMap[status] || {
        label: "Desconhecido",
        class: "bg-gray-100 text-gray-800",
      }
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].split('-').reverse().join('/');
  };

  // Função para formatar o telefone
  const formatPhone = (phone) => {
    if (!phone) return "-";
    // Remove qualquer caractere que não seja dígito
    const digits = phone.replace(/\D/g, "");

    // Formata conforme o número de dígitos
    if (digits.length === 10) {
      // Exemplo para telefone fixo: (XX) XXXX-XXXX
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(
        6,
        10
      )}`;
    } else if (digits.length === 11) {
      // Exemplo para celular: (XX) XXXXX-XXXX
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(
        7,
        11
      )}`;
    }

    // Se não atender aos padrões, retorna o valor original
    return phone;
  };

  // Função para formatar CPF ou CNPJ
  const formatCpfCnpj = (value) => {
    if (!value) return "-";
    // Remove tudo que não for dígito
    const digits = value.replace(/\D/g, "");

    if (digits.length === 11) {
      // Formata como CPF: xxx.xxx.xxx-xx
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    } else if (digits.length === 14) {
      // Formata como CNPJ: xx.xxx.xxx/xxxx-xx
      return digits.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5"
      );
    }
    // Se não tiver o tamanho esperado, retorna o valor original ou um traço
    return value;
  };

  // Função para formatar CEP
  const formatCep = (cep) => {
    if (!cep) return "-";
    // Remove todos os caracteres que não são dígitos
    const digits = cep.replace(/\D/g, "");

    // Verifica se possui 8 dígitos e aplica a máscara
    if (digits.length === 8) {
      return digits.replace(/(\d{5})(\d{3})/, "$1-$2");
    }

    // Caso não tenha 8 dígitos, retorna o valor original
    return cep;
  };

  // Função para formatar Inscrição Estadual (IE)
  // Observação: Essa formatação é apenas uma sugestão para casos com 9 ou 12 dígitos.
  // Cada estado pode ter sua própria máscara, portanto, ajuste conforme necessário.
  const formatInscricaoEstadual = (ie) => {
    if (!ie) return "-";
    // Remove caracteres não numéricos
    const digits = ie.replace(/\D/g, "");

    // Exemplo para IE com 9 dígitos: xxx.xxx.xxx
    if (digits.length === 9) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})/, "$1.$2.$3");
    }

    // Exemplo para IE com 12 dígitos: xxx.xxx.xxx.xxx
    if (digits.length === 12) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{3})/, "$1.$2.$3.$4");
    }

    // Se não atender aos padrões esperados, retorna o valor original
    return ie;
  };

  // Função para formatar SUFRAMA
  const formatSuframa = (suframa) => {
    if (!suframa) return "-";
    // Remove caracteres não numéricos
    const digits = suframa.replace(/\D/g, "");

    // Exemplo para SUFRAMA com 9 dígitos
    if (digits.length === 9) {
      return digits.replace(/(\d{8})(\d{1})/, "$1-$2");
    }

    // Se não atender ao padrão esperado, retorna o valor original
    return suframa;
  };

  const renderSection = (title, content) => (
    <div className="mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-3">{title}</h3>
      <div className="bg-gray-50 rounded-lg p-4">{content}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl my-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Detalhes do Cliente</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-1">
          {renderSection(
            "Informações Básicas",
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Código
                </label>
                <p className="mt-1">{customer.CODIGO}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Vendedor
                </label>
                <p className="mt-1">{customer.VENDEDOR_NOME}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Razão Social
                </label>
                <p className="mt-1">{customer.RAZAO_SOCIAL}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Nome Fantasia
                </label>
                <p className="mt-1">{customer.NOME_FANTASIA}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  CNPJ/CPF
                </label>
                <p className="mt-1">{formatCpfCnpj(customer.CNPJ)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Tipo
                </label>
                <p className="mt-1">
                  {customer.TIPO === "F" ? "Física" : "Revenda"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Status
                </label>
                <span
                  className={`mt-1 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    formatStatus(customer.STATUS).class
                  }`}
                >
                  {formatStatus(customer.STATUS).label}
                </span>
              </div>
            </div>
          )}

          {renderSection(
            "Contato",
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Telefone Principal
                </label>
                <p className="mt-1">{formatPhone(customer.TELEFONE_GERAL_1)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Telefone Secundário
                </label>
                <p className="mt-1">{formatPhone(customer.TELEFONE_GERAL_2)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Email
                </label>
                <p className="mt-1">{customer.EMAIL_NFE}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Site
                </label>
                <p className="mt-1">{customer.SITE.trim() || "-"}</p>
              </div>
            </div>
          )}

          {renderSection(
            "Endereço",
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500">
                  Logradouro
                </label>
                <p className="mt-1">
                  {customer.ENDERECO_RUA}, {customer.ENDERECO_NUMERO}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Bairro
                </label>
                <p className="mt-1">{customer.ENDERECO_BAIRRO}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  CEP
                </label>
                <p className="mt-1">{formatCep(customer.ENDERECO_CEP)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Complemento
                </label>
                <p className="mt-1">
                  {customer.ENDERECO_COMPLEMENTO.trim() || "-"}
                </p>
              </div>
            </div>
          )}

          {renderSection(
            "Informações Fiscais",
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Inscrição Estadual
                </label>
                <p className="mt-1">
                  {formatInscricaoEstadual(
                    customer.INSCRICAO_ESTADUAL.trim()
                  ) || "-"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Inscrição Municipal
                </label>
                <p className="mt-1">
                  {customer.INSCRICAO_MUNICIPAL.trim() || "-"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  SUFRAMA
                </label>
                <p className="mt-1">
                  {formatSuframa(customer.SUFRAMA?.trim()) || "-"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Regime Tributário
                </label>
                <p className="mt-1">
                  {customer.REGIME_TRIBUTARIO ? "Diferenciado" : "Normal"}
                </p>
              </div>
            </div>
          )}

          {renderSection(
            "Datas",
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Data de Cadastro
                </label>
                <p className="mt-1">{formatDate(customer.DATA_CADASTRO)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Última Compra
                </label>
                <p className="mt-1">
                  {formatDate(customer.DATA_ULTIMA_COMPRA)}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 pt-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
